export const getZitadelGqlConfig = () => {
    const config = __JAEN_ZITADEL_GQL__;
    if (!config.organizationId) {
        throw new Error('jaen: zitadelGql.organizationId is required to use the Zitadel API. ' +
            'It is optional only for signing in, which is plain OIDC.');
    }
    return config;
};
export const getZitadelGqlUrl = () => {
    const config = getZitadelGqlConfig();
    return config.graphqlUrl || `${config.authority.replace(/\/+$/, '')}/graphql`;
};
export class ZitadelGqlError extends Error {
    constructor(message, errors = []) {
        super(message);
        this.errors = errors;
        this.name = 'ZitadelGqlError';
    }
}
export const zitadelGqlFetch = async (options) => {
    const response = await fetch(getZitadelGqlUrl(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(options.accessToken
                ? { Authorization: `Bearer ${options.accessToken}` }
                : {})
        },
        body: JSON.stringify({
            query: options.query,
            variables: options.variables ?? {}
        })
    });
    if (!response.ok) {
        throw new ZitadelGqlError(`zitadel-gql request failed: HTTP ${response.status}`);
    }
    const payload = (await response.json());
    if (payload.errors && payload.errors.length > 0) {
        throw new ZitadelGqlError(payload.errors[0]?.message ?? 'zitadel-gql returned errors', payload.errors);
    }
    if (payload.data === undefined || payload.data === null) {
        throw new ZitadelGqlError('zitadel-gql returned no data');
    }
    return payload.data;
};
export const connectionNodes = (connection) => connection?.edges?.map(edge => edge.node) ?? [];
export const primaryProfile = (user) => connectionNodes(user.profiles)[0];
const USER_CORE_FIELDS = `
  id
  state
  userName
  loginNames
  preferredLoginName
`;
const HUMAN_DETAIL_FRAGMENT = `
  ... on HumanUser {
    preferences {
      preferredLanguage
    }
    profiles {
      edges {
        node {
          id
          email
          phone
          firstName
          lastName
          nickName
          gender
          displayName
          avatarUrl
          preferredLanguage
        }
      }
    }
    roles {
      edges {
        node {
          key
          displayName
        }
      }
    }
    authorizations {
      edges {
        node {
          id
          projectId
          projectName
          roleKeys
          state
        }
      }
    }
  }
  ... on MachineUser {
    roles {
      edges {
        node {
          key
          displayName
        }
      }
    }
    authorizations {
      edges {
        node {
          id
          projectId
          projectName
          roleKeys
          state
        }
      }
    }
  }
`;
export const fetchCurrentUserRoles = async (accessToken) => {
    const data = await zitadelGqlFetch({
        query: `
      query JaenCurrentUserRoles {
        currentUser {
          id
          ... on HumanUser {
            roles { edges { node { key } } }
            authorizations { edges { node { id projectId projectName roleKeys state } } }
          }
          ... on MachineUser {
            roles { edges { node { key } } }
            authorizations { edges { node { id projectId projectName roleKeys state } } }
          }
        }
      }
    `,
        accessToken
    });
    const user = data.currentUser;
    const plainRoles = connectionNodes(user.roles).map(role => role.key);
    const projectScopedRoles = connectionNodes(user.authorizations).flatMap(authorization => authorization.roleKeys.map(roleKey => `${authorization.projectId}:${roleKey}`));
    return { plainRoles, projectScopedRoles };
};
export const fetchCurrentUser = async (accessToken) => {
    const data = await zitadelGqlFetch({
        query: `
      query JaenCurrentUser {
        currentUser {
          ${USER_CORE_FIELDS}
          resourceOwner
          creationDate
          changeDate
          ${HUMAN_DETAIL_FRAGMENT}
        }
      }
    `,
        accessToken
    });
    return data.currentUser;
};
export const fetchUsers = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      query JaenUsers($limit: Number) {
        users(args: {limit: $limit}) {
          totalCount
          edges {
            node {
              ${USER_CORE_FIELDS}
              ... on HumanUser {
                profiles {
                  edges {
                    node {
                      id
                      email
                      phone
                      firstName
                      lastName
                      displayName
                      avatarUrl
                      preferredLanguage
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
        variables: { limit: options.limit ?? 500 },
        accessToken: options.accessToken
    });
    return {
        totalCount: data.users.totalCount ?? data.users.edges.length,
        users: connectionNodes(data.users)
    };
};
export const fetchUser = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      query JaenUser($id: String!) {
        user(args: {id: $id}) {
          ${USER_CORE_FIELDS}
          resourceOwner
          creationDate
          changeDate
          ${HUMAN_DETAIL_FRAGMENT}
        }
      }
    `,
        variables: { id: options.userId },
        accessToken: options.accessToken
    });
    return data.user;
};
export const fetchProjectRoles = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      query JaenProjectRoles($projectId: String!) {
        projectRoles(args: {projectId: $projectId}) {
          edges {
            node {
              key
              displayName
            }
          }
        }
      }
    `,
        variables: { projectId: options.projectId },
        accessToken: options.accessToken
    });
    return connectionNodes(data.projectRoles);
};
const USER_MUTATION_RESULT = `
  ok
  message
  userId
`;
export const createUser = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      mutation JaenCreateUser($values: ZitadelUserCreateInput!) {
        createUser(args: {values: $values, createProfile: true}) {
          ${USER_MUTATION_RESULT}
        }
      }
    `,
        variables: {
            values: {
                emailAddress: options.emailAddress,
                username: options.username,
                ...(options.password ? { password: options.password } : {}),
                ...(options.firstName || options.lastName
                    ? {
                        details: {
                            ...(options.firstName ? { firstName: options.firstName } : {}),
                            ...(options.lastName ? { lastName: options.lastName } : {})
                        }
                    }
                    : {})
            }
        },
        accessToken: options.accessToken
    });
    return data.createUser;
};
export const updateUser = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      mutation JaenUpdateUser($userId: String!, $changes: ZitadelUserUpdateInput!) {
        updateUser(args: {userId: $userId, changes: $changes}) {
          ${USER_MUTATION_RESULT}
        }
      }
    `,
        variables: { userId: options.userId, changes: options.changes },
        accessToken: options.accessToken
    });
    return data.updateUser;
};
const simpleUserMutation = (field) => async (options) => {
    const data = await zitadelGqlFetch({
        query: `
        mutation JaenUserMutation($userId: String!) {
          ${field}(args: {userId: $userId}) {
            ${USER_MUTATION_RESULT}
          }
        }
      `,
        variables: { userId: options.userId },
        accessToken: options.accessToken
    });
    return data[field];
};
export const deleteUser = simpleUserMutation('deleteUser');
export const deactivateUser = simpleUserMutation('deactivateUser');
export const reactivateUser = simpleUserMutation('reactivateUser');
export const lockUser = simpleUserMutation('lockUser');
export const unlockUser = simpleUserMutation('unlockUser');
export const setUserPassword = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      mutation JaenSetUserPassword($userId: String!, $newPassword: String!, $changeRequired: Boolean) {
        setUserPassword(args: {userId: $userId, newPassword: $newPassword, changeRequired: $changeRequired}) {
          ok
          message
        }
      }
    `,
        variables: {
            userId: options.userId,
            newPassword: options.newPassword,
            changeRequired: options.changeRequired ?? false
        },
        accessToken: options.accessToken
    });
    return data.setUserPassword;
};
const simpleResultMutation = (field) => async (options) => {
    const data = await zitadelGqlFetch({
        query: `
        mutation JaenUserResultMutation($userId: String!) {
          ${field}(args: {userId: $userId}) {
            ok
            message
          }
        }
      `,
        variables: { userId: options.userId },
        accessToken: options.accessToken
    });
    return data[field];
};
export const requestUserPasswordReset = simpleResultMutation('requestUserPasswordReset');
export const sendUserEmailVerification = simpleResultMutation('sendUserEmailVerification');
export const resendUserEmailVerification = simpleResultMutation('resendUserEmailVerification');
export const verifyUserEmail = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      mutation JaenVerifyUserEmail($userId: String!, $code: String!) {
        verifyUserEmail(args: {userId: $userId, code: $code}) {
          ok
          message
        }
      }
    `,
        variables: { userId: options.userId, code: options.code },
        accessToken: options.accessToken
    });
    return data.verifyUserEmail;
};
export const setUserPhone = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      mutation JaenSetUserPhone($userId: String!, $phone: String!) {
        setUserPhone(args: {userId: $userId, phone: $phone}) {
          ${USER_MUTATION_RESULT}
        }
      }
    `,
        variables: { userId: options.userId, phone: options.phone },
        accessToken: options.accessToken
    });
    return data.setUserPhone;
};
export const createAuthorization = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      mutation JaenCreateAuthorization($input: ZitadelAuthorizationCreateInput!) {
        createAuthorization(args: {input: $input}) {
          ok
          message
          authorizationId
        }
      }
    `,
        variables: {
            input: {
                userId: options.userId,
                projectId: options.projectId,
                roleKeys: options.roleKeys
            }
        },
        accessToken: options.accessToken
    });
    return data.createAuthorization;
};
export const updateAuthorization = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      mutation JaenUpdateAuthorization($input: ZitadelAuthorizationUpdateInput!) {
        updateAuthorization(args: {input: $input}) {
          ok
          message
          authorizationId
        }
      }
    `,
        variables: {
            input: {
                authorizationId: options.authorizationId,
                roleKeys: options.roleKeys
            }
        },
        accessToken: options.accessToken
    });
    return data.updateAuthorization;
};
export const deleteAuthorization = async (options) => {
    const data = await zitadelGqlFetch({
        query: `
      mutation JaenDeleteAuthorization($authorizationId: String!) {
        deleteAuthorization(args: {authorizationId: $authorizationId}) {
          ok
          message
          authorizationId
        }
      }
    `,
        variables: { authorizationId: options.authorizationId },
        accessToken: options.accessToken
    });
    return data.deleteAuthorization;
};
